import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { MainLayout } from 'views/layouts'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { useNavigate, useLocation } from 'react-router-dom'
import { Container, CompletedUsersCard, CompletedUsersHeader, DashboardContainer, ParticipantTable, Row } from './styled'
import { H2, H4, P } from 'views/components/UI/Text'
import ModalLoader from 'views/components/UI/ModalLoader/modal-loader'
import WhiteContainer from 'views/components/common/WhiteContainer'
import BluesiteTable from 'views/components/UI/BluesiteTable'
import SearchBar from 'views/components/Searchbar/search-bar'
import { useNotification } from 'lib/context/notification.context'
import { ActionViewDownloadReportProcessCreated } from 'views/components/UI/BluesiteTable/Actions/GridActions'
import { platformAdminRoutes } from 'router/routes'
import { getProcessIdFromSearch } from 'router/process-detail-id'
import { userSelector } from 'ducks/user'
import { useFetchGetResumeProcess, useFetchGetUsersByProcess } from 'hooks/process/useGetProcess/useGetProcess'
import { useGetReports } from 'hooks/process/useGetReports/useGetReports'
import sendReportToUser from 'services/reports/sendReportResultToUserEvaluated/sendReportToUser'
import AddUserIcon from 'assets/icons/AddUserIcon'
import { DEFAULT_LIMIT } from 'constants/global.constants'
import { UniqueUserIds, FilterGetUsersByProcess } from '../store'
import { fetchUpdateUserTestAction } from '../../constants/fetch_process'
import { PrimaryButton, StrokedButton } from 'views/components/UI/Buttons'
import Modal from 'views/components/UI/Modal'
import ItemDashboard from './components/ItemDashboard'
import { getLogoUrl } from '../../constants/fetch_process'
import { sendTestsToUsers } from '../../services'
import ProcessFollowUpExports from './components/ProcessFollowUpExports'
import ParticipantFollowUpSummary, { lastCompletedMeasurement } from './components/ParticipantFollowUpSummary'

type ParticipationMapType = Record<number, boolean>

const formatDateValue = (...dateCandidates: Array<string | null | undefined>) => {
  const validDate = dateCandidates.find((date) => Boolean(date))

  return validDate ? dayjs.utc(validDate).format('DD/MM/YYYY') : null
}

const DetailProcessDelivered = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const processId: number = getProcessIdFromSearch(location.search) ?? (location.state?.id as number)
  const { companyId } = useSelector(userSelector)
  const { getError, getSuccess, getWarning } = useNotification()
  const [participation, setParticipation] = useState<boolean>(false)
  const [userIds, setUserIds] = useState<Set<number>>(new Set())
  const [search, setSearch] = useState('')
  const [filterGetUsers, setFilterGetUsers] = useState<FilterGetUsersByProcess>({
    limit: DEFAULT_LIMIT,
    retrieveAllUsers: true,
    evaluatedOnly: true,
    viewUsersWithoutTests: false,
  })
  const [participationMap, setParticipationMap] = useState<ParticipationMapType>({})
  const [showFollowUpModal, setShowFollowUpModal] = useState(false)
  const [followUpMode, setFollowUpMode] = useState<'all' | 'selected'>('all')
  const [followUpUserIds, setFollowUpUserIds] = useState<Set<number>>(new Set())
  const [followUpSearch, setFollowUpSearch] = useState('')
  const [sendingFollowUp, setSendingFollowUp] = useState(false)
  const [followUpResults, setFollowUpResults] = useState<string[]>([])
  const [followUpFinished, setFollowUpFinished] = useState(false)
  const [compareFollowUpWithIdeal, setCompareFollowUpWithIdeal] = useState(false)
  const [followUpCategories, setFollowUpCategories] = useState<Set<number>>(new Set())
  const followUpRequestKeys = useRef<Record<string, string>>({})
  const { listProcessUsersResume, listProcessTestResume, fetchProcessResume } = useFetchGetResumeProcess(processId)
  const { listUsersByProcess, totalPages, fetchUsers, loadingUsersByProcess } = useFetchGetUsersByProcess(
    processId,
    filterGetUsers
  )
  const { loadingGetReports, reportsResult, handleGetReports } = useGetReports(processId)
  const participantHeaders = [
    { name: 'Documento', key: 'document' },
    { name: 'Correo', key: 'email' },
    { name: 'Nombre', key: 'name' },
    { name: 'Follow Up', key: 'follow_up_total', render: (row: any) => <ParticipantFollowUpSummary summary={row.follow_up_summary} processId={processId} onRefresh={fetchUsers} /> },
    { name: 'Última medición completada', key: 'last_measurement', render: (row: any) => lastCompletedMeasurement(row.follow_up_summary) },
  ]
  const {
    name,
    total_users: totalUsers,
    total_users_completed: totalUsersCompleted,
    total_users_uncompleted: totalUsersUncompleted,
  } = listProcessUsersResume
  const {
    test_360: isTest360,
    evaluators_count: evaluatorsCount,
    hasBetesaIdealProfile,
    hasBetesaTest,
    hasCompetencesTest,
  } = listProcessTestResume
  // A process with both assessments must choose its source explicitly; never
  // infer 3D Skills from the absence of a Betesa ideal profile.
  const availableFollowUpCategories = [
    hasBetesaTest && { id: 1, label: 'Betesa' },
    hasCompetencesTest && { id: 2, label: '3D Skills' },
  ].filter(Boolean) as Array<{ id: number; label: string }>
  const uniqueUserIds: UniqueUserIds = {}
  dayjs.extend(utc)

  const dataAllUsers = listUsersByProcess.reduce((accumulator: any[], user: any) => {
    if (!uniqueUserIds[user.userId]) {
      uniqueUserIds[user.userId] = true

      accumulator.push({
        id: user.userId,
        name: `${user.first_name} ${user.last_name}`,
        email: user.email,
        document: user.document,
        follow_up_summary: user.follow_up_summary,
        follow_up_total: user.follow_up_summary ? user.follow_up_summary.completed + user.follow_up_summary.pending : 0,
        last_measurement: user.follow_up_summary?.last_completed_date || '',
        dateSend: formatDateValue(user.send_date, user.shipping_date) || 'Sin envío',
        answer_date: formatDateValue(user.answer_date, user.reply_date, user.date_completed) || 'Sin respuesta',
      })
    }

    return accumulator
  }, [])
  const isFollowUpDisabled: boolean = !availableFollowUpCategories.length || !dataAllUsers.length

  useEffect(() => {
    if (reportsResult.status === 'success') {
      const { data } = reportsResult
      const dateZip = new Date()
      const dia = dateZip.getDate()
      const mes = dateZip.getMonth() + 1
      const año = dateZip.getFullYear()
      const url = window.URL.createObjectURL(data)

      const a = document.createElement('a')

      a.href = url

      a.download = `${name}_${dia}_${mes}_${año}.zip`

      a.click()

      window.URL.revokeObjectURL(url)

      setUserIds(new Set())
    }
  }, [name, reportsResult])

  useEffect(() => {
    fetchProcessResume()
    fetchUsers()
  }, [fetchProcessResume, fetchUsers])

  useEffect(() => {
    if (reportsResult.status === 'error') {
      getError(reportsResult.error)
    }
  }, [reportsResult, getError, getSuccess])

  const handleSetFilterGetUsers = (searchTerm: string) => {
    setFilterGetUsers({ ...filterGetUsers, search: searchTerm, page: 1 })
  }

  const handlePagination = (page: number) => {
    setFilterGetUsers({ ...filterGetUsers, limit: DEFAULT_LIMIT, page })
  }

  const handleDownloadSingleReport = (userId: number) => {
    const newSetId = new Set([userId])

    handleGetReports({ userIds: newSetId })
  }

  // const handleDownloadMultipleReports = () => {
  //   const newSetIds = new Set(userIds)
  //   handleGetReports({ userIds: newSetIds })
  // }

  const handleSendReportToMultipleUsers = async (selectedUsersIds: number[]) => {
    const userTestIds: number[] = []
    const usersIdData: number[] = []
    const allUsersIds = Array.from(selectedUsersIds)
    try {
      if (!allUsersIds.length) {
        getWarning('Debes seleccionar los usuarios que quieres autorizar')
        return
      } else {
        setParticipation(true)
        userIds.forEach((user: number) => {
          const userProcesses = listUsersByProcess.filter(
            (obj: { userId: number; processId: number }) => obj.userId === user
          )
          userProcesses.forEach((userTestId: any) => {
            userTestIds.push(userTestId.user_test_id)
          })
          usersIdData.push(user)
        })
        fetchUpdateUserTestAction(userTestIds, true)
        await sendReportToUser({ companyId: companyId, users: usersIdData })
        getSuccess('Se ha concedido el acceso a los usuarios seleccionados')
        const updatedParticipationMap: ParticipationMapType = { ...participationMap }
        selectedUsersIds.forEach((userId) => {
          updatedParticipationMap[userId] = true
        })
        setParticipationMap(updatedParticipationMap)
        setParticipation(false)
      }
    } catch (error: any) {
      getError(`Error al autorizar el acceso a los reportes ${error.message}`)
    }
  }

  const handleWatchSettings = () => {
    navigate(platformAdminRoutes.watchSettings, { state: { processId, name } })
  }

  const handleViewAnalytics = () => {
    navigate(platformAdminRoutes.Analitycs, { state: { processId, processName: name } })
  }

  const filteredUsers = listUsersByProcess.filter((user: any) => !(user.participant_report || user.access_report))
  const followUpUsers = dataAllUsers.filter((user: any) =>
    `${user.name} ${user.email} ${user.document}`.toLowerCase().includes(followUpSearch.toLowerCase())
  )
  const isButtonDisabled =
    listUsersByProcess.length === 0 ||
    !filteredUsers ||
    filteredUsers.length < 1 ||
    participation ||
    Array.from(userIds).some((userId) => participationMap[userId])

  const toggleFollowUpUser = (userId: number) => {
    const nextUserIds = new Set(followUpUserIds)
    if (nextUserIds.has(userId)) nextUserIds.delete(userId)
    else nextUserIds.add(userId)
    setFollowUpUserIds(nextUserIds)
  }

  const toggleFollowUpCategory = (categoryId: number) => {
    const nextCategories = new Set(followUpCategories)
    if (nextCategories.has(categoryId)) nextCategories.delete(categoryId)
    else nextCategories.add(categoryId)
    setFollowUpCategories(nextCategories)
  }

  const handleSendFollowUps = async () => {
    const selectedIds: number[] =
      followUpMode === 'all' ? dataAllUsers.map((user: any) => user.id) : Array.from(followUpUserIds)

    if (followUpMode !== 'all' && !selectedIds.length) {
      getWarning('Debes seleccionar al menos un usuario para enviar Follow Ups')
      return
    }
    if (!followUpCategories.size) {
      getWarning('Selecciona al menos una evaluación para el Follow Up')
      return
    }

    try {
      setSendingFollowUp(true)
      const logoResponse = await getLogoUrl(processId)
      const responses = await Promise.all(
        Array.from(followUpCategories).map((follow_up_category_id) => {
          const fingerprint = JSON.stringify([
            processId,
            follow_up_category_id,
            followUpMode,
            followUpMode === 'all' ? [] : [...selectedIds].sort(),
            compareFollowUpWithIdeal,
          ])
          const storageKey = `bluesite-follow-up:${fingerprint}`
          if (!followUpRequestKeys.current[fingerprint]) {
            followUpRequestKeys.current[fingerprint] = sessionStorage.getItem(storageKey) || crypto.randomUUID()
            sessionStorage.setItem(storageKey, followUpRequestKeys.current[fingerprint])
          }
          return sendTestsToUsers({
            process_id: processId,
            company_id: companyId,
            type: 'follow_up',
            follow_up_category_id,
            follow_up_all: followUpMode === 'all',
            follow_up_request_key: followUpRequestKeys.current[fingerprint],
            compare_with_ideal_profile: compareFollowUpWithIdeal,
            users_data: selectedIds.map((userId) => ({ user_id: userId })),
            email_template: {
              subject: 'Tienes una nueva prueba pendiente en Bluesite',
              textOne: '{{company}} te ha asignado una nueva prueba.',
              textTwo: 'Esta evaluación permitirá conocer tus resultados actuales y observar su evolución respecto a la medición anterior.',
              textThree: 'Tu cuenta de Bluesite ya está activa. Ingresa con tu correo y contraseña habituales para responder la evaluación pendiente.',
              textFour: 'Si no recuerdas tu contraseña, puedes recuperarla desde la pantalla de inicio de sesión.',
              date: dayjs.utc().add(7, 'day').format('DD/MM/YYYY'),
              logo: logoResponse?.cobranding?.logo,
            },
          }).then((response: any) => ({
            ...response,
            categoryLabel: follow_up_category_id === 2 ? '3D Skills' : 'Betesa',
          }))
        })
      )
      setFollowUpResults(responses.map((response: any) => {
        if (response.status !== 'OK')
          return `${response.categoryLabel}: error (${response.message || 'estado desconocido'}). Comprueba el estado antes de repetir.`
        const assignments = Array.isArray(response.users) ? response.users.length : 0
        const sent = Array.isArray(response.users)
          ? response.users.filter((user: { send_mail?: boolean }) => user.send_mail).length : 0
        const skipped = Array.isArray(response.skipped) ? response.skipped.length : 0
        const pending = Array.isArray(response.delivery_pending) ? response.delivery_pending.length : 0
        return `${response.categoryLabel}: ${assignments} ${assignments === 1 ? 'asignación' : 'asignaciones'}, ${sent} ${sent === 1 ? 'correo enviado' : 'correos enviados'}, ${pending} pendientes y ${skipped} personas omitidas.`
      }))
      const failedResponse = responses.find((response: any) => response.status !== 'OK')
      responses.forEach((response: any) => {
        if (response.status === 'OK' && response.delivery_pending?.length)
          getWarning(
            `${response.categoryLabel}: asignaciones creadas; hay correos pendientes. Usa Reintentar correo sin consumir cupos adicionales.`
          )
      })
      if (!failedResponse) {
        const pendingCount = responses.reduce(
          (total: number, response: any) => total + (Array.isArray(response.delivery_pending) ? response.delivery_pending.length : 0),
          0
        )
        if (pendingCount > 0) {
          getWarning(`${pendingCount} envíos de Follow Up requieren revisión. No reintentes ni devuelvas sus créditos hasta verificarlos.`)
          return
        }
        Object.keys(followUpRequestKeys.current).forEach((key) =>
          sessionStorage.removeItem(`bluesite-follow-up:${key}`)
        )
        followUpRequestKeys.current = {}
        const sentCount = responses.reduce(
          (total: number, response: any) => total + (Array.isArray(response.users) ? response.users.filter((user: { send_mail?: boolean }) => user.send_mail).length : 0),
          0
        )
        const skippedCount = responses.reduce(
          (total: number, response: any) => total + (Array.isArray(response.skipped) ? response.skipped.length : 0),
          0
        )
        if (sentCount === 0) {
          getWarning('No se envió ningún Follow Up. Revisa las personas omitidas.')
          return
        }
        getSuccess(
          `Se enviaron ${sentCount} Follow Up${sentCount === 1 ? '' : 's'}${
            skippedCount
              ? `; ${skippedCount} usuario${skippedCount === 1 ? '' : 's'} omitido${skippedCount === 1 ? '' : 's'}`
              : ''
          }`
        )
        setFollowUpFinished(true)
        setFollowUpUserIds(new Set())
        setFollowUpCategories(new Set())
        setCompareFollowUpWithIdeal(false)
      } else {
        const otherAssignmentsCreated = responses.some(
          (response: any) => response.status === 'OK' && Array.isArray(response.users) && response.users.length > 0
        )
        getError(
          `${failedResponse?.categoryLabel}: ${
            failedResponse?.message || 'No fue posible enviar esta evaluación'
          }${otherAssignmentsCreated ? '. Las asignaciones creadas para la otra evaluación se conservan.' : ''}`
        )
      }
    } catch (error: any) {
      getError(error.message || 'No fue posible enviar los Follow Ups')
    } finally {
      setSendingFollowUp(false)
      fetchUsers()
      fetchProcessResume()
    }
  }

  return (
    <MainLayout>
      {loadingGetReports && (
        <ModalLoader
          style={{ backgroundColor: 'white', padding: '30px', borderRadius: '30px' }}
          text="Generando reportes..."
          isLoading={loadingGetReports}
        />
      )}
      <Container>
        <Row>
          <H2>{name || ''}</H2>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'nowrap', justifyContent: 'flex-end' }}>
            <PrimaryButton onClick={() => {
              setFollowUpResults([])
              setFollowUpFinished(false)
              setShowFollowUpModal(true)
            }} type="button" disabled={isFollowUpDisabled}>
              Enviar Follow Ups
            </PrimaryButton>
            <PrimaryButton onClick={handleWatchSettings} type="button">
              Ver parametrización
            </PrimaryButton>
            <PrimaryButton onClick={handleViewAnalytics} type="button">
              Ver analíticas
            </PrimaryButton>
          </div>
        </Row>
        <DashboardContainer>
          <WhiteContainer
            padding="18px 20px"
            width="70%"
            height="100px"
            style={{ boxSizing: 'border-box' }}
            flexDirection="row"
            alignItems="center"
            justifyContent="space-around"
          >
            <ItemDashboard text="Total de pruebas enviadas" quantity={totalUsers ?? 0} />
            <ItemDashboard showDivider text="Pruebas completadas" quantity={totalUsersCompleted ?? 0} />
            <ItemDashboard
              showDivider
              highlight
              color="#FFFFFF"
              text="Pruebas sin completar"
              quantity={totalUsersUncompleted ?? 0}
              onClick={() => navigate(platformAdminRoutes.UsersWithIncompleteTests, { state: listProcessUsersResume })}
            />
            {isTest360 && (
              <ItemDashboard
                showDivider
                text="Evaluadores"
                quantity={evaluatorsCount}
                onClick={() => navigate(platformAdminRoutes.EvaluatorsTest360, { state: listProcessUsersResume })}
              />
            )}
          </WhiteContainer>
        </DashboardContainer>
        <ProcessFollowUpExports processId={processId} hasSkills={!!hasCompetencesTest} hasBetesa={!!hasBetesaTest} />
        <P role="note" margin="0 0 16px" color="#526b7d" fontSize="13px">Las analíticas utilizan la última medición completada y activa de cada participante, por tipo de prueba. Un Follow Up pendiente no reemplaza los resultados anteriores. Los informes y comparativos conservan las mediciones seleccionadas.</P>
        <CompletedUsersCard>
          <CompletedUsersHeader>
            <H4>Participantes del proceso</H4>
            <SearchBar
              label="Buscar por nombre, correo, documento"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                handleSetFilterGetUsers(e.target.value)
              }}
              onClick={() => handleSetFilterGetUsers(search)}
            />
            <Row justifyContent="flex-end" alignItems="center">
              <AddUserIcon disabled={isButtonDisabled} />
              <P
                disabled={isButtonDisabled}
                onClick={() => {
                  if (!isButtonDisabled) {
                    handleSendReportToMultipleUsers(Array.from(userIds))
                  } else {
                    getWarning('Los usuarios tienen configurada la descarga de reporte automática')
                  }
                }}
                style={{ cursor: 'pointer' }}
                margin="0px 6px"
                color="#2D749C"
                fontWeight={700}
                fontSize="14px"
              >
                {dataAllUsers.length === userIds.size
                  ? 'Autorizar acceso a todos los usuarios'
                  : 'Autorizar acceso a usuarios'}
              </P>
            </Row>
          </CompletedUsersHeader>

          <P margin="0 0 16px" color="#526b7d" fontSize="13px">El resumen cuenta solo Follow Up de Betesa y 3D Skills, no las mediciones originales. Los informes y comparativos están en la ficha de cada participante.</P>
          <ParticipantTable><BluesiteTable
            loading={loadingUsersByProcess}
            headers={participantHeaders}
            data={dataAllUsers}
            tableStyle="process-detail"
            searchBar={false}
            searchLabel=""
            totalPages={totalPages}
            currentPage={filterGetUsers.page ? filterGetUsers.page : 1}
            handlePagination={handlePagination}
            ActionComponent={ActionViewDownloadReportProcessCreated}
            checks={true}
            selectedItems={userIds}
            setSelectedItems={(e) => setUserIds(e)}
            extraDataAction={{
              listIdUsers: userIds,
              onDownloadReport: (e: number) => handleDownloadSingleReport(e),
              processId,
              historyLabel: 'Ver historial',
            }}
          /></ParticipantTable>
        </CompletedUsersCard>
      </Container>
      <Modal
        show={showFollowUpModal}
        setShow={setShowFollowUpModal}
        styleContent={{ width: '560px', maxWidth: 'calc(100% - 32px)', maxHeight: '80vh', overflowY: 'auto' }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <H4>Enviar Follow Ups</H4>
          <P margin="0">
            Se recomienda realizar la nueva medición después de seis meses. Esta recomendación no bloquea el envío.
          </P>
          <label style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input
              type="radio"
              name="follow-up-mode"
              checked={followUpMode === 'all'}
              onChange={() => setFollowUpMode('all')}
            />
            Enviar a todos los usuarios elegibles del proceso
          </label>
          <label style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input
              type="radio"
              name="follow-up-mode"
              checked={followUpMode === 'selected'}
              onChange={() => setFollowUpMode('selected')}
            />
            Seleccionar usuarios
          </label>
          {followUpMode === 'selected' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <input
                type="search"
                placeholder="Buscar por nombre, correo o documento"
                value={followUpSearch}
                onChange={(event) => setFollowUpSearch(event.target.value)}
                style={{ padding: '10px', border: '1px solid #C2C2C2', borderRadius: '6px' }}
              />
              <div style={{ maxHeight: '260px', overflowY: 'auto', border: '1px solid #E1E1E1', borderRadius: '6px' }}>
                {followUpUsers.map((user: any) => (
                  <label
                    key={user.id}
                    style={{
                      display: 'flex',
                      gap: '10px',
                      alignItems: 'center',
                      padding: '10px 12px',
                      borderBottom: '1px solid #F0F0F0',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={followUpUserIds.has(user.id)}
                      onChange={() => toggleFollowUpUser(user.id)}
                    />
                    <span>
                      {user.name} · {user.email}
                    </span>
                  </label>
                ))}
                {!followUpUsers.length && <P margin="12px">No se encontraron usuarios.</P>}
              </div>
              <P margin="0">Usuarios seleccionados: {followUpUserIds.size}</P>
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <P margin="0">Evaluaciones a repetir</P>
            {availableFollowUpCategories.map(({ id, label }) => (
              <label key={id} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={followUpCategories.has(id)}
                  onChange={() => toggleFollowUpCategory(id)}
                />
                {label}
              </label>
            ))}
          </div>
          {hasBetesaIdealProfile && followUpCategories.has(1) && (
            <label style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={compareFollowUpWithIdeal}
                onChange={(event) => setCompareFollowUpWithIdeal(event.target.checked)}
              />
              Contrastar los resultados del Follow Up con el perfil ideal del proceso
            </label>
          )}
          {followUpResults.length > 0 && (
            <div role="status" aria-live="polite">
              <strong>Resultado del último intento</strong>
              <ul>{followUpResults.map((result) => <li key={result}>{result}</li>)}</ul>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <StrokedButton onClick={() => setShowFollowUpModal(false)} disabled={sendingFollowUp}>
              {followUpFinished ? 'Cerrar' : 'Cancelar'}
            </StrokedButton>
            <PrimaryButton
              onClick={handleSendFollowUps}
              disabled={sendingFollowUp || followUpFinished || !availableFollowUpCategories.length}
            >
              {sendingFollowUp ? 'Enviando...' : 'Enviar Follow Ups'}
            </PrimaryButton>
          </div>
        </div>
      </Modal>
    </MainLayout>
  )
}

export default DetailProcessDelivered
